import { Component, Input, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, firstValueFrom, filter } from 'rxjs';
import { Division } from 'src/app/core/services/ebr-api-client/ebr-api-client.g';
import { FiltersService } from 'src/app/core/services/filters.service';
import { MixpanelService } from 'src/app/core/services/mixpanel.service';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { ServiceCustomerFilterOptions } from 'src/app/global/service-customer-model';
import { ServiceInsightsService } from '../../services/service-insights-service.service';
import { ServiceInsightsDetailsStore } from '../../stores/service-insights-details.store';
import { Moment } from 'moment';
import {
  PestServiceInsightsCallData,
  ServiceInsightRecord,
  ServiceInsightsMetricCount,
  ServiceInsightsMetricCountsSummary,
  ServiceMetricTypes,
  SICallTypes,
} from '../../types/service-insight-types';

/**
 * Component representing a service metric card that displays insights related to a specific service metric.
 * It fetches and displays data based on the selected filters and allows users to select or deselect the metric for presentation.
 *
 * Remarks: Leveraged by divisions which do not have graph representation of metric data over selected period - Institutional, QSR and FRS.
 */
@Component({
    selector: 'service-metric-card',
    templateUrl: './service-metric-card.component.html',
    styleUrls: ['./service-metric-card.component.scss'],
    standalone: false
})
export class ServiceMetricCardComponent implements OnInit, OnChanges, OnDestroy {
  SICallTypes = SICallTypes;

  @Input() metricTitle: string = 'Title';
  @Input() serviceMetricType: ServiceMetricTypes = ServiceMetricTypes.Calls;
  @Input() metricKey: SICallTypes = SICallTypes.UntrackedCalls;
  @Input() isEnableCheckBox: boolean = false;
  @Input() summaryMetric: ServiceInsightsMetricCount | undefined;
  @Input() trainingBreakdown: ServiceInsightsMetricCountsSummary['trainingBreakdown'] | undefined;

  selectAllSubscription: Subscription | undefined;
  serviceCustomerFiltersSubscription: Subscription | undefined;

  metricData: ServiceInsightRecord[] | undefined;
  filters: ContractModel | undefined;
  selectedAdvancedFilters: ServiceCustomerFilterOptions = {
    accountNumber: [],
    accountName: [],
    country: [],
    state: [],
    city: [],
    zipCode: [],
    recurringIssue: [],
  };

  isLoading: boolean = true;
  checked: boolean = false;
  totalMetricCount: number = 0;
  totalLocationCount: number = 0;
  percentGrowth: number = 100;
  growthDateRangeAvailable: boolean = false;
  initialized: boolean = false;
  serviceMetricId: string = '';
  periodRange: { start: Moment; end: Moment } | undefined;
  avgPerLocation: number = 0;
  isBreakdownVisible: boolean = false;
  private _trainingBreakdownColumns: Array<{ title: string; value: ServiceInsightsMetricCount; description: string }> = [];
  division: Division | undefined;

  Division = Division;

  constructor(
    private _filterService: FiltersService,
    private _serviceInsightsService: ServiceInsightsService,
    private _mixpanelService: MixpanelService,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _siDetailsStore: ServiceInsightsDetailsStore,
  ) {}

  isDataEmpty(data: ServiceInsightRecord[]) {
    return data.length == 0;
  }

  async ngOnInit() {
    this.selectedAdvancedFilters = {
      accountNumber: [],
      accountName: [],
      country: [],
      state: [],
      city: [],
      zipCode: [],
      recurringIssue: [],
    };

    // The filters BehaviorSubject can start as undefined; wait for the first defined value
    // so this.division is reliably set before we branch on it (Pest vs non-Pest).
    const f = await firstValueFrom(
      this._filterService.selectedFiltersObservable.pipe(
        filter((value): value is ContractModel => value != undefined),
      ),
    );

    this.filters = f;
    this.division = f.division as Division;
    this.periodRange = this._filterService.getSelectedPeriodRange(f.period);
    this.serviceMetricId = this.metricTitle
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .toLowerCase()
      .trim();
    this.selectAllSubscription =
      this._serviceInsightsService.isAllSelected.subscribe((allSelected) => {
        this.checked = allSelected;
        this.onCheckedChange(this.checked);
      });

    this.isLoading = true;

    await this.loadData();
    this.growthDateRangeAvailable =
      this._filterService.IsPreviousPeriodAvailable();

    // Refreshed UI for non-Pest SI relies on the async metric-counts-summary Input; keep the skeleton visible until it arrives.
    // Pest will be consolidated differently, so the skeleton loader logic does not apply to it.
    this.isLoading = !this.summaryMetric && this.division != Division.Pest;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['summaryMetric'] && this.summaryMetric) {
      this.setSummaryMetricData();
      this.isLoading = false;
    }

    if (changes['trainingBreakdown']) {
      this.buildTrainingBreakdownColumns();
    }
  }

  async loadData() {
    switch (this.division) {
      // TODO: US618430 refactor to use similar pattern non-Pest divisions as well once API contract is updated.
      case Division.Pest:
        this.subscribeToPestServiceData();
        await this.fetchPestServiceData();
        break;
      default:
        if(this.summaryMetric)
        {
          this.setSummaryMetricData();
        }
        break;
    }

    if (this.metricData != undefined && !this.isDataEmpty(this.metricData)) {
      this.setUpData();
    }
  }

  private setSummaryMetricData() {
    this.totalMetricCount = this.summaryMetric?.total ?? 0;
    this.percentGrowth = this.summaryMetric?.percentageChange ?? 0;
    this.avgPerLocation = this.summaryMetric?.avgPerLocation ?? 0;
    this.growthDateRangeAvailable = this._filterService.IsPreviousPeriodAvailable();
    this.onCheckedChange(this.checked);
  }

  /**
   * Subscribes to the service insights data based on the service type and key. It updates the component's data and growth properties when new data is received.
   * If the service type is 'Calls', it subscribes to the call data for the specific key. Otherwise, it subscribes to the observation data.
   */
  subscribeToPestServiceData() {
    this._serviceInsightsService
      .getPestServiceInsightsCallData(
        this.metricKey as keyof PestServiceInsightsCallData,
      )
      .subscribe((d) => {
        this.metricData = d?.periods;
        this.percentGrowth = d?.percentChangeInCount;
        this.totalLocationCount = d?.distinctLocationsCount;
      });
  }

  /**
   * Fetches the pest service insights data from the API for Pest Division based on the service type and key. If the data is empty, it fetches new data from the API and updates the service insights service with the new data.
   * After fetching the data, it tracks a Mixpanel event for the loaded metric.
   * @returns A promise that resolves when the data is loaded and the service insights service is updated.
   */
  async fetchPestServiceData() {
    if (this.metricData != undefined && this.isDataEmpty(this.metricData)) {
      this._serviceInsightsService.setPestServiceInsightsCallData(
        this.metricKey as keyof PestServiceInsightsCallData, //should fail
        await this._serviceInsightsService.getSIMetricByPeriodFromAPI(
          this.serviceMetricType,
          this.metricKey as SICallTypes,
          this.selectedAdvancedFilters,
        ),
      );
      this.trackMixpanelEvent();
    }
  }

  private trackMixpanelEvent() {
    this._mixpanelService.track(
      'Service Insights- ' + this.metricKey + ' Loaded',
      {
        ServiceType: this.serviceMetricType,
      },
    );
  }

  /**
   * Calculates and sets up the total metric count, total location count,
   * average per location, and growth properties based on the current metric data.
   * It also handles the checkbox state based on the current data.
   */
  setUpData() {
    // Total metric count across all days in this period
    this.totalMetricCount = this.metricData!.reduce(
      (sum, current) => sum + current.total,
      0,
    );

    // Calculate averages, growth, and handle checkbox state based on the current data
    this.avgPerLocation =
      this.totalLocationCount > 0
        ? Number((this.totalMetricCount / this.totalLocationCount).toFixed(1))
        : 0;

    this.onCheckedChange(this.checked);
  }

  /**
   * Handles changes to the checkbox state. If the checkbox is checked and enabled, it adds the metric to the presentation in the service insights service. If unchecked, it removes the metric from the presentation.
   * It also checks if the total count is defined before performing any actions.
   * Cards with no checkbox (isEnableCheckBox = false) don't participate in the "Add All" selection state at all.
   * @param isChecked - The new state of the checkbox (true for checked, false for unchecked).
   */
  onCheckedChange(isChecked: boolean) {
    this.checked = isChecked;
    if (this.totalMetricCount != undefined && this.isEnableCheckBox) {
      if (this.checked) {
        this._serviceInsightsService.addMetricToPresentation(
          this.metricTitle,
          this.totalMetricCount,
        );
      } else {
        this._serviceInsightsService.removeMetricFromPresentation(
          this.metricTitle,
        );
      }
    }
    if (this.division === Division.Pest && this.isEnableCheckBox) {
      this._serviceInsightsService.setPestMetricsSelected(this.metricTitle, isChecked);
    }
  }

  onClickViewDetails() {
    this._mixpanelService.track(
      'Service Insights- ' + this.metricKey + ' View Details Clicked',
      {
        ServiceType: this.serviceMetricType,
      },
    );
    const metric = this.metricTitle.split(' ').join('-').toLowerCase();
    this._siDetailsStore.setSelectedServiceInsightsMetric(this.metricKey);
    this._router.navigate(['./' + metric], {
      relativeTo: this._activatedRoute,
    });
  }

  onClickTrainingBreakdown() {
    if (this.metricKey === SICallTypes.TrainingSessions && this.trainingBreakdown) {
      this.isBreakdownVisible = !this.isBreakdownVisible;
    }
  }

  onClickOutsideTrainingBreakdown() {
    this.isBreakdownVisible = false;
  }

  get trainingBreakdownColumns() {
    return this._trainingBreakdownColumns;
  }

  trackByColumnTitle(index: number, column: { title: string }) {
    return column.title;
  }

  private buildTrainingBreakdownColumns() {
    if (!this.trainingBreakdown) {
      this._trainingBreakdownColumns = [];
      return;
    }

    this._trainingBreakdownColumns = [
      {
        title: 'Training Sessions',
        value: this.trainingBreakdown.trainingSessions,
        description: 'Unique Training Calls + In-Service Training Calls',
      },
      {
        title: 'Dedicated Training Calls',
        value: this.trainingBreakdown.dedicatedTrainingCalls,
        description: 'Dedicated Training Calls',
      },
      {
        title: 'Training Topics',
        value: this.trainingBreakdown.trainingTopics,
        description: 'Training Topics',
      },
      {
        title: 'In Service Training',
        value: this.trainingBreakdown.inServiceTraining,
        description: 'Calls with In-Service Training',
      },
    ];
  }

  ngOnDestroy() {
    this.selectAllSubscription?.unsubscribe();
    this.serviceCustomerFiltersSubscription?.unsubscribe();
  }
}
